import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { surveysApi, Survey } from '@/api/surveys';
import { testsApi, Test } from '@/api/tests';
import ConcludeAnimation from '@/components/animations/ConcludeAnimation';
import SurveyAssignWizard from '@/components/assignments/SurveyAssignWizard';
import TestAssignWizard from '@/components/assignments/TestAssignWizard';
import { ClipboardText, DocumentText } from '@/components/icons';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { AppDialogLayout } from '@/components/system';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { useModalContext } from '@/components/ui/modal-provider';
import { Skeleton } from '@/components/ui/skeleton';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { Patient } from '@/types/patient';
import { toTitleCase } from '@/utils/toTitleCase';

interface PatientCreatedAssignModalProps {
    patient: Patient;
}

const renderSkeletonRows = (count: number) =>
    Array.from({ length: count }).map((_, index) => (
        <div
            key={`skeleton-${index}`}
            className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
        >
            <div className="w-full flex-1 space-y-2 sm:pr-4">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-8 w-full sm:w-24" />
        </div>
    ));

export default function PatientCreatedAssignModal({ patient }: PatientCreatedAssignModalProps) {
    const { closeModal } = useModalContext();
    const [openSection, setOpenSection] = useState<string>('');
    const [activeTest, setActiveTest] = useState<Test | null>(null);
    const [activeSurvey, setActiveSurvey] = useState<Survey | null>(null);
    const [step, setStep] = useState<'success' | 'assign-test' | 'assign-survey'>('success');
    const [assignedTestIds, setAssignedTestIds] = useState<string[]>([]);
    const [assignedSurveyIds, setAssignedSurveyIds] = useState<string[]>([]);
    const sendTestAccess = useFeatureEntitlement('tests.send');
    const sendSurveyAccess = useFeatureEntitlement('surveys.send');

    const shouldLoadTests = openSection === 'tests';
    const shouldLoadSurveys = openSection === 'surveys';

    const { data: tests = [], isLoading: isLoadingTests } = useQuery<Test[]>({
        queryKey: ['tests', 'list'],
        queryFn: testsApi.list,
        enabled: shouldLoadTests,
        initialData: [],
    });

    const { data: surveys = [], isLoading: isLoadingSurveys } = useQuery<Survey[]>({
        queryKey: ['surveys'],
        queryFn: surveysApi.list,
        enabled: shouldLoadSurveys,
        initialData: [],
    });

    const patientName = useMemo(() => {
        if (!patient?.full_name) return '';
        return toTitleCase(patient.full_name);
    }, [patient?.full_name]);

    const handleAssignTest = (test: Test) => {
        if (sendTestAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(sendTestAccess.decision));
            return;
        }

        setActiveSurvey(null);
        setActiveTest(test);
        setStep('assign-test');
    };

    const handleAssignSurvey = (survey: Survey) => {
        if (sendSurveyAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(sendSurveyAccess.decision));
            return;
        }

        setActiveTest(null);
        setActiveSurvey(survey);
        setStep('assign-survey');
    };

    const handleExitWizard = () => {
        setActiveTest(null);
        setActiveSurvey(null);
        setStep('success');
    };

    const handleAssignedTest = (testId: string) => {
        setAssignedTestIds((prev) => (prev.includes(testId) ? prev : [...prev, testId]));
    };

    const handleAssignedSurvey = (surveyId: string) => {
        setAssignedSurveyIds((prev) => (prev.includes(surveyId) ? prev : [...prev, surveyId]));
    };

    if (step === 'assign-test' && activeTest) {
        return (
            <TestAssignWizard
                test={activeTest}
                preSelectedPatient={patient}
                onClose={handleExitWizard}
                allowPatientSelection={false}
                onAssigned={handleAssignedTest}
            />
        );
    }

    if (step === 'assign-survey' && activeSurvey) {
        return (
            <SurveyAssignWizard
                survey={activeSurvey}
                preSelectedPatient={patient}
                onClose={handleExitWizard}
                allowPatientSelection={false}
                onAssigned={handleAssignedSurvey}
            />
        );
    }

    return (
        <AppDialogLayout
            footer={
                <div className="flex justify-end">
                    <Button onClick={closeModal} className="w-full sm:w-auto">
                        Fechar
                    </Button>
                </div>
            }
        >
            <div className="space-y-5 sm:space-y-6">
                <div className="space-y-3 text-center">
                    <div className="flex items-center justify-center">
                        <div className="relative flex items-center justify-center">
                            <div className="size-40 sm:size-56">
                                <ConcludeAnimation className="size-40 sm:size-56" />
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <PatientAvatar
                                    name={patient?.full_name || 'Paciente'}
                                    avatarUrl={patient?.avatar_url}
                                    className="size-20 shadow ring-4 ring-white sm:size-24"
                                    fallbackClassName="text-lg"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <h3 className="text-xl font-semibold text-foreground">
                            {patientName
                                ? `Paciente ${patientName} criado com sucesso!`
                                : 'Paciente criado com sucesso!'}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                            Aproveite este momento para atribuir instrumentos e formulários ao
                            paciente.
                        </p>
                    </div>
                </div>

                <div className="rounded-xl border border-border bg-surface/60">
                    <Accordion
                        type="single"
                        collapsible
                        value={openSection}
                        onValueChange={(value) => {
                            setOpenSection(value);
                            if (value !== 'tests') setActiveTest(null);
                            if (value !== 'surveys') setActiveSurvey(null);
                        }}
                        className="px-3 sm:px-4"
                    >
                        <AccordionItem value="tests">
                            <AccordionTrigger className="text-base">
                                <div className="flex items-center gap-2">
                                    <ClipboardText className="size-4 text-brand" />
                                    Instrumentos
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-3">
                                    {isLoadingTests ? (
                                        renderSkeletonRows(3)
                                    ) : tests.length === 0 ? (
                                        <p className="py-4 text-center text-sm text-muted-foreground">
                                            Nenhum instrumento disponível no momento.
                                        </p>
                                    ) : (
                                        tests.map((test) => {
                                            const isAssigned = assignedTestIds.includes(test.id);
                                            return (
                                                <div
                                                    key={test.id}
                                                    className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                                                >
                                                    <div className="min-w-0 sm:pr-4">
                                                        <p className="truncate text-sm font-semibold text-foreground">
                                                            {toTitleCase(test.name)}
                                                        </p>
                                                        {test.description && (
                                                            <p className="line-clamp-2 text-xs text-muted-foreground">
                                                                {test.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <Button
                                                        size="sm"
                                                        className={`w-full shrink-0 justify-center sm:w-auto sm:min-w-[104px] ${
                                                            isAssigned
                                                                ? 'cursor-not-allowed border border-border bg-muted text-muted-foreground'
                                                                : 'border border-brand/15 bg-brand-subtle text-brand hover:bg-brand-subtle/80 hover:text-brand'
                                                        }`}
                                                        onClick={() => handleAssignTest(test)}
                                                        disabled={isAssigned}
                                                        aria-disabled={
                                                            isAssigned || sendTestAccess.shouldBlock
                                                        }
                                                    >
                                                        {isAssigned ? 'Enviado' : 'Enviar'}
                                                        {!isAssigned &&
                                                        sendTestAccess.isUnavailableInPlan &&
                                                        sendTestAccess.availableFromPlan ? (
                                                            <FeatureUnavailableChip
                                                                availablePlan={
                                                                    sendTestAccess.availableFromPlan
                                                                }
                                                            />
                                                        ) : null}
                                                    </Button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="surveys">
                            <AccordionTrigger className="text-base">
                                <div className="flex items-center gap-2">
                                    <DocumentText className="size-4 text-brand" />
                                    Formulários
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-3">
                                    {isLoadingSurveys ? (
                                        renderSkeletonRows(3)
                                    ) : surveys.length === 0 ? (
                                        <p className="py-4 text-center text-sm text-muted-foreground">
                                            Nenhum formulário disponível no momento.
                                        </p>
                                    ) : (
                                        surveys.map((survey) => {
                                            const isAssigned = assignedSurveyIds.includes(
                                                survey.id,
                                            );
                                            return (
                                                <div
                                                    key={survey.id}
                                                    className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                                                >
                                                    <div className="min-w-0 sm:pr-4">
                                                        <p className="truncate text-sm font-semibold text-foreground">
                                                            {toTitleCase(survey.name)}
                                                        </p>
                                                    </div>
                                                    <Button
                                                        size="sm"
                                                        className={`w-full shrink-0 justify-center sm:w-auto sm:min-w-[104px] ${
                                                            isAssigned
                                                                ? 'cursor-not-allowed border border-border bg-muted text-muted-foreground'
                                                                : 'border border-brand/15 bg-brand-subtle text-brand hover:bg-brand-subtle/80 hover:text-brand'
                                                        }`}
                                                        onClick={() => handleAssignSurvey(survey)}
                                                        disabled={isAssigned}
                                                        aria-disabled={
                                                            isAssigned ||
                                                            sendSurveyAccess.shouldBlock
                                                        }
                                                    >
                                                        {isAssigned ? 'Enviado' : 'Enviar'}
                                                        {!isAssigned &&
                                                        sendSurveyAccess.isUnavailableInPlan &&
                                                        sendSurveyAccess.availableFromPlan ? (
                                                            <FeatureUnavailableChip
                                                                availablePlan={
                                                                    sendSurveyAccess.availableFromPlan
                                                                }
                                                            />
                                                        ) : null}
                                                    </Button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            </div>
        </AppDialogLayout>
    );
}
